import useRecaptcha from "../../hooks/useRecaptcha";
import downloadRepository from "../../data/repositories-repo/download";
import { parseUrl, stringifyUrl } from "query-string";
import atomWithSessionStorage from "../../utils/atomWithSessionStorage";
import { useAtom } from "jotai";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Image from "../image";

import "./index.css";

const downloadVerificationAtom = atomWithSessionStorage(
  "downloadVerification",
  { success: false },
);

const fromatUrl = (url, signatureHash) => {
  const parsedUrl = parseUrl(url);
  parsedUrl.query = {
    ...parsedUrl.query,
    Policy: signatureHash.Policy,
    Signature: signatureHash.Signature,
    "Key-Pair-Id": signatureHash["Key-Pair-Id"],
  };
  return stringifyUrl(parsedUrl);
};

const extLinkRegEx = /(https?|s?ftp):\/\/|mailto:/g;

const Link = ({
  children,
  href = "/",
  restricted,
  header,
  secondary,
  type = "default",
  className,
  iconAlign = "left",
  icon,
  image,
  ...restProps
}) => {
  const isExternal = useRef(href && href.search(extLinkRegEx) !== -1);

  const classNames = useMemo(() => {
    const classNames = ["bccampus-link", type];
    if (className) classNames.push(className);

    if (secondary !== true) classNames.push("primary");
    else classNames.push("secondary");

    if (header === true) classNames.push("header");

    return classNames.join(" ");
  }, [type, header, secondary, className]);

  const calculatedProps = {
    ...restProps,
    className: classNames,
    href: href || "/",
    role: "link",
  };

  const calculatedChildren = (
    <>
      {iconAlign === "right" && type !== "icon" && <span>{children}</span>}
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          className={`bccampus-link-icon align-${iconAlign}`}
          size={type === "icon" ? "lg" : undefined}
        />
      )}
      {image && (
        <Image
          icon
          data={image}
          className={`bccampus-link-icon align-${iconAlign}`}
          size={type === "icon" ? "2x" : undefined}
        />
      )}
      {iconAlign === "left" && type !== "icon" && <span>{children}</span>}
    </>
  );

  return isExternal.current === true ? (
    restricted === true ? (
      <RestrictedLink {...calculatedProps}>{calculatedChildren}</RestrictedLink>
    ) : (
      <a {...calculatedProps}>{calculatedChildren}</a>
    )
  ) : (
    <RouterLink {...calculatedProps} to={calculatedProps.href}>
      {calculatedChildren}
    </RouterLink>
  );
};

const RestrictedLink = ({ href, children, ...restProps }) => {
  const { execute } = useRecaptcha();
  const [downloadVerification, setDownloadVerification] = useAtom(
    downloadVerificationAtom,
  );
  const [targetUrl, setTargetUrl] = useState(href || "/");

  // Intercept click event for restricted urls
  const handleClick = async (e) => {
    if (
      downloadVerification.success === false ||
      downloadVerification.expires <= Math.floor(new Date().getTime() / 1000)
    ) {
      const targetLink = e.currentTarget;

      e.preventDefault();
      e.stopPropagation();

      execute("file_download", async (token) => {
        const validation = await downloadRepository.verify(token);
        if (validation.success === true && validation.score >= 0.5) {
          setDownloadVerification(validation);
          targetLink.href = fromatUrl(href, validation.signatureHash);
          targetLink.click();
        }
      });
    }
    return true;
  };

  // Replace restricted link URL with a signed URL
  useEffect(() => {
    if (
      downloadVerification.success === true &&
      downloadVerification.score >= 0.5 &&
      downloadVerification.expires > Math.floor(new Date().getTime() / 1000) &&
      !!downloadVerification.signatureHash
    ) {
      setTargetUrl(fromatUrl(href, downloadVerification.signatureHash));
    } else {
      setTargetUrl(href || "/");
    }
  }, [href, downloadVerification]);

  return (
    <a {...restProps} href={targetUrl} onClick={handleClick}>
      {children}
    </a>
  );
};

export default Link;
